import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { appMicrositeAPIService } from '~/api/microsites/app';

// --- Types ---
interface AppInformation {
  appName: string;
  developerCompany: string;
  description: string;
  website: string;
}

interface PlatformLinks {
  google: string;
  apple: string;
}

interface Fonts {
  title: string;
  text: string;
}

interface Stats {
  isAutogeneratedUrl: boolean;
  isLimitsEnabled: boolean;
  url: string;
  scan: string;
}

interface MicrositeStyles {
  container: Record<string, any>;
}

interface AppsState {
  title: string;
  bacgkroundColor: string[];
  appInformation: AppInformation;
  platformLinks: PlatformLinks;
  logo: string;
  fonts: Fonts;
  stats: Stats;
  micrositeData: any;
  loading: boolean;
  error: any;
  micrositeStyles: MicrositeStyles;
  data?: any;
}

// --- Initial State ---
const initialState: AppsState = {
  title: '',
  bacgkroundColor: ['#5356FF'],
  appInformation: {
    appName: 'Lorem ipsum dolor',
    developerCompany: 'Lorem ipsum',
    description: 'Vestibulum commodo vel risus ac dapibus. Mauris sollicitudin suscipit velit.',
    website: 'https://www.myweb.com',
  },
  platformLinks: {
    google: 'https://www.google.com',
    apple: 'https://www.apple.com',
  },
  logo: '',
  fonts: {
    title: 'Times New Roman, serif',
    text: 'Times New Roman, serif',
  },
  stats: {
    isAutogeneratedUrl: true,
    isLimitsEnabled: false,
    url: '',
    scan: '',
  },
  micrositeData: null,
  loading: false,
  error: null,
  micrositeStyles: { container: {} },
};

// --- Thunk ---
export const updateAppSiteThunk = createAsyncThunk<any, any>(
  'appsSlice/updateModelThunk',
  async (body, { rejectWithValue }) => {
    try {
      const response = await appMicrositeAPIService.updateAppSite(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

// --- Slice ---
export const appsSlice = createSlice({
  name: 'appsSlice',
  initialState,
  reducers: {
    setBackgroundColor(state, action: PayloadAction<string[]>) {
      state.bacgkroundColor = action.payload;
    },
    setAppInformation(state, action: PayloadAction<{ field: keyof AppInformation; value: string }>) {
      state.appInformation[action.payload.field] = action.payload.value;
    },
    setLogo(state, action: PayloadAction<string>) {
      state.logo = action.payload;
    },
    setPlatformLinks(state, action: PayloadAction<{ field: keyof PlatformLinks; value: string }>) {
      state.platformLinks[action.payload.field] = action.payload.value;
    },
    setFonts(state, action: PayloadAction<{ field: keyof Fonts; value: string }>) {
      state.fonts[action.payload.field] = action.payload.value;
    },
    setTitle(state, action: PayloadAction<string>) {
      state.title = action.payload;
    },
    setStatsData(state, action: PayloadAction<Stats>) {
      state.stats = action.payload;
    },
    setStats(state, action: PayloadAction<{ field: keyof Stats; value: string | boolean }>) {
      state.stats[action.payload.field] = action.payload.value;
    },
    setMicrositeData(state, action: PayloadAction<any>) {
      state.micrositeData = action.payload;
    },
    setRenderedMicrositeData(state, action: PayloadAction<Partial<Omit<AppsState, 'loading' | 'error'>>>) {
      Object.assign(state, {
        ...state,
        ...action.payload,
      });
    },
    setMicrositeStyles(state, action: PayloadAction<MicrositeStyles>) {
      state.micrositeStyles = action.payload;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateAppSiteThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAppSiteThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(updateAppSiteThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setBackgroundColor,
  setAppInformation,
  setLogo,
  setPlatformLinks,
  setFonts,
  setTitle,
  setStatsData,
  setStats,
  setMicrositeData,
  setRenderedMicrositeData,
  setMicrositeStyles,
  resetState,
} = appsSlice.actions;

export default appsSlice.reducer;
